import { truncateName, type AssetRelationType, type LeadType, type Ownership } from '@aikoa/common'
import { useTranslations, type TFunction } from '@aikoa/i18n/react'
import { cva } from 'class-variance-authority'
import { useMemo } from 'react'
import { View } from 'react-native'
import { Text as RNText } from 'react-native'
import Animated from 'react-native-reanimated'
import Icon, { type IconNameProps } from '~/components/Icon'
import designTokens from '~/lib/designTokens'
import { cn } from '~/lib/utils'

const badgeVariants = cva(
  'flex-row items-center justify-center rounded-full transition-all duration-150 ease-out',
  {
    variants: {
      position: {
        primary: 'z-10',
        secondary: 'rounded-r-full',
        single: 'rounded-full',
      },
      variant: {
        default: '',
        inverted: 'bg-transparent',
      },
      size: {
        sm: '',
        md: 'py-0.5',
      },
    },
    compoundVariants: [
      {
        position: 'primary',
        variant: 'default',
        size: 'md',
        className: '-mr-5 border border-secondary/20 bg-tertiary-foreground px-3',
      },
      {
        position: 'primary',
        variant: 'default',
        size: 'sm',
        className: 'px-3',
      },
      {
        position: 'primary',
        variant: 'inverted',
        size: 'sm',
        className: 'px-0',
      },
      {
        position: 'secondary',
        variant: 'default',
        className: 'border border-transparent bg-secondary',
      },
      {
        position: 'secondary',
        variant: 'default',
        size: 'sm',
        className: 'border-0 px-2 pl-4',
      },
      {
        position: 'secondary',
        variant: 'default',
        size: 'md',
        className: 'px-3 pl-7',
      },
      {
        position: 'single',
        variant: 'default',
        size: 'md',
        className: 'px-3 pl-3',
      },
    ],
    defaultVariants: {
      position: 'primary',
      variant: 'default',
      size: 'md',
    },
  }
)

const textVariants = cva('select-none whitespace-nowrap font-medium', {
  variants: {
    position: {
      primary: 'text-secondary',
      secondary: '',
      single: 'text-secondary',
    },
    variant: {
      default: 'text-primary',
      inverted: 'text-secondary/90',
    },
  },
  compoundVariants: [
    {
      position: 'secondary',
      variant: 'default',
      className: 'text-tertiary-foreground',
    },
    {
      position: 'secondary',
      variant: 'inverted',
      className: 'text-secondary/90',
    },
  ],
  defaultVariants: {
    position: 'primary',
    variant: 'default',
  },
})

type BadgeSize = 'sm' | 'md'
type BadgeVariant = 'default' | 'inverted'

type ItemTypeBadgeBaseProps = {
  ownership?: Ownership
  className?: string
  badgeClassName?: string
  textClassName?: string
  expandDirection?: 'left' | 'right'
  size?: BadgeSize
  variant?: BadgeVariant
  organisationName?: string | null
  realtorName?: string | null
}

type AssetTypeBadgeProps = ItemTypeBadgeBaseProps & {
  itemKind?: 'asset'
  type?: AssetRelationType
}

type LeadTypeBadgeProps = ItemTypeBadgeBaseProps & {
  itemKind: 'lead'
  type?: LeadType
}

export type ItemTypeBadgeProps = AssetTypeBadgeProps | LeadTypeBadgeProps
export type PropertyTypeBadgeProps = AssetTypeBadgeProps
export type LeadTypeBadgeComponentProps = Omit<LeadTypeBadgeProps, 'itemKind'>

function getAssetTypeIcon(type: AssetRelationType): IconNameProps {
  switch (type) {
    case 'representation':
      return { name: 'sign-real-estate', library: 'materialcommunityicons' }
    case 'reference':
      return { name: 'book-outline', library: 'materialcommunityicons' }
    case 'prospect':
      return { name: 'flower-outline', library: 'materialcommunityicons' }
  }
}

function getLeadTypeIcon(type: LeadType): IconNameProps {
  switch (type) {
    case 'buyer':
      return { name: 'account-cash-outline', library: 'materialcommunityicons' }
  }
}

function getAssetTypeLabel(type: AssetRelationType, t: TFunction<'property'>): string {
  switch (type) {
    case 'representation':
      return t(($) => $.relationTypes.representation)
    case 'reference':
      return t(($) => $.relationTypes.reference)
    case 'prospect':
      return t(($) => $.relationTypes.prospect)
  }
}

function getLeadTypeLabel(type: LeadType, t: TFunction<'leads'>): string {
  switch (type) {
    case 'buyer':
      return t(($) => $.types.buyer)
  }
}

function getOwnershipIcon(ownership: Ownership): IconNameProps | undefined {
  switch (ownership) {
    case 'own':
      return {
        name: 'account-outline',
        library: 'materialcommunityicons',
      }
    case 'organisation':
      return {
        name: 'account-group-outline',
        library: 'materialcommunityicons',
      }
    case 'network':
      return { name: 'globe-outline', library: 'ionicons' }
    case 'purchased':
      return { name: 'account-cash-outline', library: 'materialcommunityicons' }
  }
}

function getOwnershipLabelByKind({
  ownership,
  organisationName,
  realtorName,
  t,
}: {
  ownership: Ownership
  organisationName?: string | null
  realtorName?: string | null
  t: TFunction<'leads'>
}): string | undefined {
  switch (ownership) {
    case 'own':
      return t(($) => $.ownership.own)
    case 'organisation': {
      if (organisationName && realtorName) {
        return `${organisationName} / ${realtorName}`
      }

      if (organisationName) {
        return organisationName
      }

      return t(($) => $.ownership.organisation)
    }
    case 'network':
      return t(($) => $.ownership.network)
    case 'purchased':
      return t(($) => $.ownership.purchased)
    default:
      return undefined
  }
}

type BadgeItem = {
  icon: IconNameProps
  label: string
}

export function ItemTypeBadge(props: ItemTypeBadgeProps) {
  const {
    ownership,
    className,
    badgeClassName,
    size = 'md',
    variant = 'default',
    organisationName,
    realtorName,
    textClassName,
  } = props
  const itemKind = props.itemKind ?? 'asset'
  const { t: tProperty } = useTranslations('property')
  const { t: tLeads } = useTranslations('leads')

  const typeItem = useMemo((): BadgeItem | null => {
    if (!props.type) {
      return null
    }

    if (itemKind === 'lead') {
      return {
        icon: getLeadTypeIcon(props.type as LeadType),
        label: getLeadTypeLabel(props.type as LeadType, tLeads),
      }
    }

    return {
      icon: getAssetTypeIcon(props.type as AssetRelationType),
      label: getAssetTypeLabel(props.type as AssetRelationType, tProperty),
    }
  }, [props.type, itemKind, tProperty, tLeads])

  const ownershipItem = useMemo((): BadgeItem | null => {
    if (!ownership) {
      return null
    }

    const icon = getOwnershipIcon(ownership)

    if (!icon) {
      return null
    }

    const label = getOwnershipLabelByKind({
      ownership,
      organisationName: truncateName(organisationName ?? '', 18, 1),
      realtorName: truncateName(realtorName ?? '', 16, 2),
      t: tLeads,
    })

    if (!label) {
      return null
    }

    return {
      icon,
      label,
    }
  }, [ownership, organisationName, realtorName, tLeads])

  const hasBothItems = typeItem !== null && ownershipItem !== null

  if (!typeItem && !ownershipItem) {
    return null
  }

  const iconSize = size === 'sm' ? 14 : 17

  const getIconColor = (isPrimary: boolean, isOnlyItem: boolean) => {
    if (variant === 'default') {
      if (isOnlyItem) {
        return designTokens.tertiaryForeground
      }

      return isPrimary ? designTokens.secondary : designTokens.tertiaryForeground
    }

    return designTokens.secondary
  }

  return (
    <View
      className={cn(
        'group/badge flex-row items-center',
        size === 'sm' ? 'gap-3' : 'gap-4',
        variant === 'default' && 'gap-0',
        className
      )}>
      {typeItem && (
        <BadgeIconView
          className={badgeClassName}
          icon={typeItem.icon}
          label={typeItem.label}
          iconSize={iconSize}
          iconColor={getIconColor(true, !hasBothItems)}
          position="primary"
          variant={variant}
          size={size}
          isSingle={!hasBothItems}
          textClassName={textClassName}
        />
      )}

      {ownershipItem && (
        <BadgeIconView
          className={badgeClassName}
          icon={ownershipItem.icon}
          label={ownershipItem.label}
          iconSize={iconSize}
          iconColor={getIconColor(false, !hasBothItems)}
          position="secondary"
          variant={variant}
          size={size}
          isSingle={!hasBothItems}
          textClassName={textClassName}
        />
      )}
    </View>
  )
}

export function PropertyTypeBadge(props: PropertyTypeBadgeProps) {
  return <ItemTypeBadge {...props} itemKind="asset" />
}

export function LeadTypeBadge(props: LeadTypeBadgeComponentProps) {
  return <ItemTypeBadge {...props} itemKind="lead" />
}

type BadgeIconViewProps = {
  icon: IconNameProps
  label: string
  iconSize: number
  iconColor: string
  position: 'primary' | 'secondary' | 'single'
  variant: BadgeVariant
  size: BadgeSize
  isSingle: boolean
  className?: string
  textClassName?: string
}

function BadgeIconView({
  icon,
  label,
  iconSize,
  iconColor,
  position,
  variant,
  size,
  isSingle,
  className,
  textClassName,
}: BadgeIconViewProps) {
  return (
    <View
      className={cn(
        badgeVariants({ position, variant, size }),
        isSingle && 'mr-0 px-3',
        isSingle && variant === 'inverted' && 'px-0',
        className
      )}>
      <Icon
        size={iconSize}
        {...icon}
        className={cn(
          'transition-colors duration-150 ease-out',
          icon.name === 'account-outline' && '-ml-0.5',
          icon.name === 'globe-outline' && '-ml-px',
          textClassName
        )}
        color={iconColor}
      />

      <Animated.View className={cn('pointer-events-none pl-1')}>
        <RNText
          numberOfLines={1}
          className={`${textVariants({ position, variant })} ${textClassName ?? ''} ${size === 'sm' ? 'text-xxs' : 'text-xs'}`}>
          {label}
        </RNText>
      </Animated.View>
    </View>
  )
}
